import { RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react'
import * as React from 'react'
import { type DayContentProps, DayPicker } from 'react-day-picker'

import { buttonVariants } from '@/components/ui/button'
import { useDisabledDays } from '@/hooks/use-disabled-days'
import { cn } from '@/lib/utils'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  withDisabledDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker> & { withDisabledDays?: boolean }) {
  const { disabledDays, disabledDayOfWeek } = useDisabledDays()

  return (
    <DayPicker
      disabled={withDisabledDays ? [...disabledDays, { dayOfWeek: disabledDayOfWeek }] : undefined}
      showOutsideDays={showOutsideDays}
      className={cn('p-4', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'flex flex-col gap-4',
        caption: 'flex justify-center pt-1 relative items-center w-full',
        caption_label: 'text-sm font-medium',
        nav: 'flex items-center gap-1',
        nav_button: cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), 'p-0'),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-x-1',
        head_row: 'flex',
        head_cell: 'text-grey-500 rounded-md w-10 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-transparent [&:has([aria-selected].day-range-end)]:rounded-r-md',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md'
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-10 p-0 font-normal rounded-full aria-selected:opacity-100 active:bg-blue-200'
        ),
        day_range_start: 'day-range-start aria-selected:bg-blue-200 aria-selected:text-white-100',
        day_range_end: 'day-range-end aria-selected:bg-blue-200 aria-selected:text-white-100',
        day_selected:
          'bg-blue-200 text-white-100 hover:bg-blue-200 hover:text-white-100 focus:bg-blue-200 focus:text-white-100',
        day_today: 'text-blue-200 border border-blue-200 hover:border-none hover:text-inherit transition-all',
        day_outside: 'day-outside text-grey-500',
        day_disabled: 'text-grey-500 opacity-90',
        day_range_middle: 'aria-selected:bg-blue-200 aria-selected:text-white-100',
        day_hidden: 'invisible',
        ...classNames
      }}
      components={{
        IconLeft: ({ className }) => <RiArrowLeftSLine className={cn('size-3.5', className)} />,
        IconRight: ({ className }) => <RiArrowRightSLine className={cn('size-3.5', className)} />,
        DayContent: CustomDayContent
      }}
      {...props}
    />
  )
}

export { Calendar }

function CustomDayContent(props: DayContentProps) {
  const { date, activeModifiers } = props
  const isDisabled = activeModifiers.disabled

  return (
    <div className='relative flex flex-col items-center'>
      <span className={isDisabled ? 'text-grey-500 opacity-90' : ''}>{date.getDate()}</span>
      {isDisabled && (
        <span className='absolute -bottom-1 left-1/2 size-[5px] -translate-x-1/2 rounded-full bg-blue-200' />
      )}
    </div>
  )
}
